// IocRowDetails.jsx 🧠💥✨ Clean Layout: Tags left / Details + Flags right / Footer Full
import React, { useState } from "react";
import { toast } from "react-toastify";
import UserInfoModal from "./UserInfoModal";
import SmoothCollapse from "./SmoothCollapse";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import EditIocModal from "./EditIocModal";

const IocRowDetails = ({ ioc, isExpanded, onDelete, onEditSuccess, userCountryCode }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [justAcknowledged, setJustAcknowledged] = useState(false);

  const created = new Date(ioc.created_at).toLocaleString();
  const lastMod = ioc.updated_at ? new Date(ioc.updated_at).toLocaleString() : "Never";
  const tagsArray = ioc.tags ? ioc.tags.split(",").map(t => t.trim()) : [];
  const acknowledgedBy = Array.isArray(ioc.acknowledged_by) ? ioc.acknowledged_by : [];

  const alreadyAcknowledged =
    justAcknowledged || acknowledgedBy.some(ack => ack.country_code?.toUpperCase() === userCountryCode?.toUpperCase());

  const countryToTwemoji = (code) => {
    if (!code) return null;
    return [...code.toUpperCase()]
      .map(c => 127397 + c.charCodeAt())
      .map(cp => cp.toString(16))
      .join("-");
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(ioc.id);
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("🔥 Server error while deleting IoC.");
    } finally {
      setIsDeleting(false);
      setShowConfirmModal(false);
    }
  };

  const requestDownloadToken = async (filename) => {
    try {
      const res = await fetch(`/api/request_token.php?file=${encodeURIComponent(filename)}`);
      const data = await res.json();
      if (data.success && data.token) {
        window.open(`/api/download_attachment.php?token=${data.token}`, "_blank");
      } else {
        toast.error(data.error || "Unable to generate download link.");
      }
    } catch (err) {
      console.error("Token request error:", err);
      toast.error("Server error while requesting download.");
    }
  };

  const handleAcknowledge = async () => {
    try {
      const res = await fetch("/api/acknowledge_ioc.php", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: `ioc_id=${encodeURIComponent(ioc.id)}`,
      });

      const data = await res.json();

      if (data.success) {
        setJustAcknowledged(true);
        onEditSuccess?.("IoC acknowledged");
      } else {
        toast.error(data.error || "Acknowledge failed");
      }
    } catch (err) {
      console.error("Acknowledge error:", err);
      toast.error("Server error during acknowledge");
    }
  };

  const handleEmailSend = () => {
    const subject = `IoC Report: ${ioc.ioc_value}`;
    const body = `Details of the IoC:

- Value: ${ioc.ioc_value}
- Type: ${ioc.ioc_type}
- Description: ${ioc.description || "—"}
- Criticality: ${ioc.criticality || "—"}
- Generated by: ${ioc.generated_by || "—"}
- Affected Country: ${ioc.affected_country || "—"}`;

    const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
  };

  return (
    <>
      <tr>
        <td colSpan="5" className="p-0" style={{ backgroundColor: "#1e1e1e" }}>
          <SmoothCollapse show={isExpanded}>
            <div className="card mb-3" style={{ backgroundColor: "#1e1e1e", borderRadius: "8px", border: "none", padding: "10px 20px" }}>
              {/* HEADER */}
              <div className="card-header d-flex justify-content-between align-items-center"
                style={{ backgroundColor: "#282727", borderBottom: "1px solid #b87a46", color: "#929292" }}>
                <div>
                  <div className="d-flex flex-column" style={{ fontSize: "13px", color: "#929292" }}>
                    <span>
                      <i className="bi bi-calendar-event me-1" style={{ color: "#b87a46" }}></i>
                      <strong>Published:</strong> {created}
                    </span>
                    <span>
                      <i className="bi bi-pencil-square me-1" style={{ color: "#b87a46" }}></i>
                      <strong>Last Modified:</strong> {lastMod}
                    </span>
                  </div>
                </div>
                <div style={{ color: "#b3947a", display: "flex", alignItems: "center", gap: "8px" }}>
                  <strong>Affected Country:</strong>
                  {ioc.affected_country ? (
                    <img src={`https://cdnjs.cloudflare.com/ajax/libs/twemoji/14.0.2/svg/${countryToTwemoji(ioc.affected_country)}.svg`}
                      alt="Flag" style={{ width: "28px", height: "20px" }} />
                  ) : "—"}
                </div>
              </div>

              {/* BODY */}
              <div className="card-body text-light">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="badge" style={{ backgroundColor: "#1e1e1e", border: "1px solid #6c757d" }}>
                    {ioc.ioc_value}
                  </span>
                  <button className="badge text-light border-0"
                    style={{ backgroundColor: "#282727", cursor: "pointer" }}
                    onClick={() => setModalOpen(true)}>
                    {ioc.generated_by || "Unknown"}
                  </button>
                </div>

                <div className="mt-4" style={{
                  backgroundColor: "#2c2c2c",
                  borderLeft: "4px solid #6c757d",
                  padding: "15px 20px",
                  borderRadius: "6px"
                }}>
                  <div className="d-flex align-items-center mb-2">
                    <i className="bi bi-card-text me-2" style={{ color: "#6c757d" }}></i>
                    <h6 className="mb-0 text-light fw-bold">Description</h6>
                  </div>
                  <div style={{ color: "#aaa", fontSize: "14px", lineHeight: "1.6" }}
                    dangerouslySetInnerHTML={{ __html: ioc.description || "—" }} />
                </div>

                {ioc.status === "closed" && ioc.closing_comment && (
                  <div className="mt-4" style={{
                    backgroundColor: "#2b2b2b",
                    borderLeft: "4px solid #925a2a",
                    padding: "15px 20px",
                    borderRadius: "6px"
                  }}>
                    <div className="d-flex align-items-center mb-2">
                      <i className="bi bi-chat-dots-fill me-2" style={{ color: "#c77a3a" }}></i>
                      <h6 className="mb-0 fw-bold text-light">Closing Comment</h6>
                    </div>
                    <div style={{ color: "#aaa", fontSize: "14px", lineHeight: "1.6" }}
                      dangerouslySetInnerHTML={{ __html: ioc.closing_comment }} />
                  </div>
                )}

                <div className="row mt-4">
                  <div className="col-md-6">
                    <div className="mb-2 fw-bold" style={{ color: "#b87a46" }}>Tags:</div>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      {tagsArray.length > 0 ? tagsArray.map((tag, i) => (
                        <span key={i} className="badge" style={{
                          backgroundColor: "#1e1e1e", border: "1px solid #6c757d"
                        }}>{tag}</span>
                      )) : (
                        <span style={{ fontSize: "14px", color: "#aaa" }}>—</span>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6 text-end">
                    <div className="mb-2 fw-bold" style={{ color: "#b87a46" }}>Details:</div>
                    <div style={{ fontSize: "14px", color: "#f8f9fa", marginBottom: "1rem" }}>
                      <strong>Criticality:</strong>{" "}
                      <span className="badge bg-info text-dark bg-opacity-75">{ioc.criticality || "—"}</span><br />
                      <strong>Related to Incident:</strong>{" "}
                      {ioc.related_to_incident ? "Yes" : "No"}
                    </div>
                    {acknowledgedBy.length > 0 && (
                      <div>
                        <div className="mb-1 fw-bold" style={{ color: "#b87a46" }}>
                          Acknowledged by :
                        </div>
                        <div className="d-flex justify-content-end flex-wrap gap-2">
                          {acknowledgedBy.map((ack, idx) => (
                            <img
                              key={idx}
                              src={`https://cdnjs.cloudflare.com/ajax/libs/twemoji/14.0.2/svg/${countryToTwemoji(ack.country_code)}.svg`}
                              alt={ack.country_code}
                              data-bs-toggle="tooltip"
                              data-bs-placement="top"
                              title={`${ack.display_name || 'Unknown'}`}
                              style={{ width: "24px", height: "16px", cursor: "pointer" }}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="card-footer d-flex align-items-center flex-wrap gap-2" style={{
                backgroundColor: "#282727", borderTop: "1px solid #b87a46", padding: "12px 20px"
              }}>
                {ioc.file_attachment && (
                  <button className="btn btn-sm" style={{ backgroundColor: "#1e1e1e", color: "#b87a46" }}
                    onClick={() => requestDownloadToken(ioc.file_attachment)}>
                    <i className="bi bi-download me-1"></i> Attachment
                  </button>
                )}
                {!alreadyAcknowledged && (
                  <button className="btn btn-sm d-flex align-items-center gap-2"
                    style={{ backgroundColor: "#1e1e1e", color: "#b87a46" }}
                    onClick={handleAcknowledge}
                  >
                    Acknowledge
                    {userCountryCode && (
                      <img
                        src={`https://cdnjs.cloudflare.com/ajax/libs/twemoji/14.0.2/svg/${countryToTwemoji(userCountryCode)}.svg`}
                        alt={userCountryCode}
                        title={userCountryCode}
                        style={{ width: "20px", height: "14px" }}
                      />
                    )}
                  </button>
                )}
                {ioc.virusTotalUrl && (
                  <a href={ioc.virusTotalUrl} target="_blank" rel="noreferrer"
                    className="btn btn-sm" style={{ backgroundColor: "#1e1e1e", color: "#b87a46" }}>
                    <i className="bi bi-virus"></i> VirusTotal
                  </a>
                )}
                {["ip", "domain"].includes(ioc.ioc_type?.toLowerCase()) && ioc.abuseIPDBUrl && (
                  <a href={ioc.abuseIPDBUrl} target="_blank" rel="noreferrer"
                    className="btn btn-sm" style={{ backgroundColor: "#1e1e1e", color: "#b87a46" }}>
                    <i className="bi bi-exclamation-triangle"></i> AbuseIPDB
                  </a>
                )}
                {["domain", "url"].includes(ioc.ioc_type?.toLowerCase()) && ioc.webCheckUrl && (
                  <a href={ioc.webCheckUrl} target="_blank" rel="noreferrer"
                    className="btn btn-sm" style={{ backgroundColor: "#1e1e1e", color: "#b87a46" }}>
                    <i className="bi bi-search"></i> WebCheck
                  </a>
                )}
                <div style={{ marginLeft: "auto" }}>
                  <button
                    className="btn btn-sm me-2"
                    style={{ backgroundColor: "#b3947a", color: "#1e1e1e" }}
                    onClick={handleEmailSend}
                    title="Send this IoC via email"
                  >
                    <i className="bi bi-envelope-paper"></i>
                  </button>
                  <button className="btn btn-sm me-2" style={{ backgroundColor: "#b3947a", color: "#1e1e1e" }}>
                    <i className="bi bi-send-plus"></i>
                  </button>
                  <button
                    className="btn btn-sm me-2"
                    style={{ backgroundColor: "#b3947a", color: "#1e1e1e" }}
                    onClick={() => setShowEditModal(true)}
                    title="Edit this IoC"
                  >
                    <i className="bi bi-pencil-square"></i>
                  </button>
                  <button
                    className="btn btn-sm"
                    style={{ backgroundColor: "#b3947a", color: "#1e1e1e" }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowConfirmModal(true);
                    }}
                    title="Delete this IoC"
                    disabled={isDeleting}
                  >
                    <i className="bi bi-trash3"></i>
                  </button>
                </div>
              </div>
            </div>
          </SmoothCollapse>
        </td>
      </tr>

      <UserInfoModal displayName={ioc.generated_by} show={modalOpen} onHide={() => setModalOpen(false)} />
      <EditIocModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        ioc={ioc}
        onSuccess={() => {
          setShowEditModal(false);
          onEditSuccess?.("IoC updated successfully!");
        }}
      />
      <ConfirmDeleteModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        onConfirm={handleDelete}
        deleting={isDeleting}
      />
    </>
  );
};

export default IocRowDetails;
